// src/pages/admin/property/room/adminEdit.tsx

import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { EditableFormField } from '@/components/admin/EditableFormField';
import { ImageUpload } from '@/components/admin/ImageUpload';
import { useRoomEditor } from '@/hooks/admin/useRoomEditor';
import { FloorPlanUpload } from '@/components/admin/FloorPlanEditor';
import ChevronLeftIcon from '@/image/common/ChevronLeftIcon';

export const AdminRoomEdit: React.FC = () => {
  const { propertyId, roomId } = useParams<{
    propertyId: string;
    roomId: string;
  }>();

  const {
    formData,
    propertyName,
    images,
    imageIds,
    loading,
    handleInputChange,
    handleAddImages,
    handleRemoveImage,
    handleSaveAll,
  } = useRoomEditor(propertyId, roomId);

  if (loading) {
    return <div>Loading...</div>;
  }
  if (!propertyId || !roomId) {
    return <div>propertyId または roomId が指定されていません。</div>;
  }

  let previewUrl: string | undefined;
  if (formData.floor_plan_image) {
    if (formData.floor_plan_image instanceof File) {
      previewUrl = URL.createObjectURL(formData.floor_plan_image);
    } else if (
      'url' in formData.floor_plan_image &&
      formData.floor_plan_image.url
    ) {
      previewUrl = formData.floor_plan_image.url;
    }
  }

  return (
    <AdminLayout>
      <div className="flex items-center pb-6 border-b border-gray-400">
        <span className="bg-gray-50 border border-black p-2 rounded">
          <Link to={`/admin/property/${roomId}/room`}>
            <ChevronLeftIcon />
          </Link>
        </span>
        <span className="text-2xl ml-3">
          {propertyName}/{formData.room_type_name}
        </span>
      </div>

      <div className="p-6 bg-yellow">
        <EditableFormField
          label="タイプ名"
          field="room_type_name"
          subLabel1="(日本語)"
          defaultValue1={formData.room_type_name}
          subLabel2="(英語)"
          defaultValue2={formData.room_type_name_en}
          height={23}
          onValueChange={(values) => {
            handleInputChange('room_type_name', values.value1 || '');
            handleInputChange('room_type_name_en', values.value2 || '');
          }}
        />

        <EditableFormField
          label="タイプ情報"
          field="room_area"
          subLabel1="面積"
          defaultValue1={formData.room_area}
          subLabel2="間取"
          defaultValue2={formData.floor_plan}
          height={23}
          onValueChange={(values) => {
            handleInputChange('room_area', values.value1 || '');
            handleInputChange('floor_plan', values.value2 || '');
          }}
        />

        <EditableFormField
          label="物件情報"
          field="information"
          subLabel1="(日本語)"
          defaultValue1={formData.information}
          subLabel2="(英語)"
          defaultValue2={formData.information_en}
          height={23}
          onValueChange={(values) => {
            handleInputChange('information', values.value1 || '');
            handleInputChange('information_en', values.value2 || '');
          }}
        />

        <EditableFormField
          label="ファシリティ"
          field="facility"
          subLabel1="(日本語)"
          defaultValue1={formData.facility}
          subLabel2="(英語)"
          defaultValue2={formData.facility_en}
          height={23}
          onValueChange={(values) => {
            handleInputChange('facility', values.value1 || '');
            handleInputChange('facility_en', values.value2 || '');
          }}
        />

        <EditableFormField
          label="概要"
          field="description"
          subLabel1="(日本語)"
          defaultValue1={formData.description}
          subLabel2="(英語)"
          defaultValue2={formData.description_en}
          height={23}
          onValueChange={(values) => {
            handleInputChange('description', values.value1 || '');
            handleInputChange('description_en', values.value2 || '');
          }}
        />

        <EditableFormField
          label="MS料金"
          field="price"
          subLabel1="(共通)"
          defaultValue1={formData.price}
          height={23}
          onValueChange={(values) => {
            handleInputChange('price', values.value1 || '');
          }}
        />

        <EditableFormField
          label="アメニティ"
          field="bath_amenity"
          subLabel1="(日本語)"
          defaultValue1={formData.bath_amenity}
          subLabel2="(英語)"
          defaultValue2={formData.bath_amenity_en}
          height={23}
          onValueChange={(values) => {
            handleInputChange('bath_amenity', values.value1 || '');
            handleInputChange('bath_amenity_en', values.value2 || '');
          }}
        />

        <ImageUpload
          label="写真"
          // 既存画像は、images と imageIds を組み合わせて渡す
          images={images.map((url, idx) => ({
            id: imageIds[idx] != null ? imageIds[idx]! : idx,
            url,
          }))}
          onAddImages={handleAddImages}
          onRemoveImage={handleRemoveImage}
        />

        <FloorPlanUpload
          imageUrl={previewUrl}
          onAddImage={(file: File) => {
            handleInputChange('floor_plan_image', file);
          }}
        />

        <button
          type="button"
          onClick={handleSaveAll}
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded"
        >
          保存
        </button>
      </div>
    </AdminLayout>
  );
};

export default AdminRoomEdit;
